import Footer from '@components/Footer';
import SobreV2 from '@/components/v2/sobre/SobreV2';
import ScrollToTop from '@components/ScrollToTop';
import AdminDashboardView from '@pages/dashboard';
import FAQ from '@pages/FAQ';
import Home from '@pages/home/homeView';
import Sobre from '@pages/Sobre';
import Subscriptions from '@pages/subscriptions';
import { useEffect } from 'react';
import {
  BrowserRouter,
  Link,
  Navigate,
  Route,
  Routes,
  useLocation,
} from 'react-router-dom';

import { CookieConsent } from '@/components/consent/CookieConsent';
import { HeaderView } from '@/components/header';
import HomeV2 from '@/components/v2/home/HomeV2';
import { initializeGoogleAnalytics } from '@/lib/googleAnalytics';
import AcervoPreview from '@/pages/__AcervoPreview';
import AdminLoginPage from '@/pages/admin/AdminLoginPage';
import Assinantes from '@/pages/admin/Assinantes';
import Comprovantes from '@/pages/admin/Comprovantes';
import EventosEspeciais from '@/pages/admin/EventosEspeciais';
import GerenciadorAddons from '@/pages/admin/GerenciadorAddons';
import GestaoEventos from '@/pages/admin/GestaoEventos';
import InscricoesPorEvento from '@/pages/admin/InscricoesPorEvento';
import InscricoesPorEventoEspecial from '@/pages/admin/InscricoesPorEventoEspecial';
import LilyMembers from '@/pages/admin/LilyMembers';
import Livros from '@/pages/admin/Livros';
import Pedidos from '@/pages/admin/Pedidos';
import Produtos from '@/pages/admin/Produtos';
import CadastroPage from '@/pages/auth/CadastroPage';
import EsqueciSenhaPage from '@/pages/auth/EsqueciSenhaPage';
import LoginPage from '@/pages/auth/LoginPage';
import RedefinirSenhaPage from '@/pages/auth/RedefinirSenhaPage';
import CheckoutView from '@/pages/checkout/CheckoutView';
import PagamentoLink from '@/pages/checkout/PagamentoLink';
import EventoEspecial from '@/pages/evento';
import ContaAssinatura from '@/pages/minha-conta/Assinatura';
import ContaDadosESenha from '@/pages/minha-conta/DadosESenha';
import ContaMeusEventos from '@/pages/minha-conta/MeusEventos';
import MinhaContaLayout from '@/pages/minha-conta/MinhaContaLayout';
import ContaPedidos from '@/pages/minha-conta/Pedidos';
import ContaVisaoGeral from '@/pages/minha-conta/VisaoGeral';

import { useAuth } from './context/AuthContext';

function RotaProtegida({ children }) {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-white">
        <div className="animate-spin h-8 w-8 border-4 border-wine border-t-transparent rounded-full" />
      </div>
    );
  }

  if (!user) return <Navigate to="/admin/login" replace />;
  if (user.role !== 'ADMIN') return <Navigate to="/" replace />;

  return children;
}

function RotaUsuario({ children }) {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-white">
        <div className="border-wine h-8 w-8 animate-spin rounded-full border-4 border-t-transparent" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location.pathname }} replace />;
  }

  return children;
}

/**
 * Redireciona uma rota extinta para a seção equivalente da home,
 * preservando a âncora que vier junto.
 *
 * /faq#quanto-custa-participar era link válido e precisa continuar
 * caindo naquela pergunta, não só na seção. Um <Navigate to="/#duvidas">
 * jogaria fora o hash de entrada e o link morreria pela metade.
 */
function ParaSecaoDaHome({ padrao }) {
  const { hash } = useLocation();
  return <Navigate to={`/${hash || padrao}`} replace />;
}

function AppContent() {
  const location = useLocation();

  // Inicializar Google Analytics
  useEffect(() => {
    initializeGoogleAnalytics();
  }, []);

  const isDashboardRoute = location.pathname.startsWith('/admin');
  const isEventoRoute = location.pathname.startsWith('/evento');
  const ocultarLayoutLegado = isDashboardRoute || isEventoRoute;
  const ocultarHeader = ocultarLayoutLegado;
  const ocultarFooter = ocultarLayoutLegado;

  return (
    <>
      {!ocultarHeader && <HeaderView />}

      {/* Banner de consentimento de cookies */}
      <CookieConsent />

      <main className="flex-grow w-full max-w-full overflow-x-hidden">
        <Routes>
          {/* Público — servido pelo redesign v2 desde 30/09/2026. */}
          <Route path="/" element={<HomeV2 />} />
          {/* Sobre e FAQ desceram para a home. As rotas viram
              redirecionamento, e não 404: quem tem o link salvo, quem
              achou no buscador e quem recebeu por WhatsApp continua
              chegando no conteúdo. */}
          <Route path="/sobre" element={<SobreV2 />} />
          <Route path="/faq" element={<ParaSecaoDaHome padrao="#duvidas" />} />
          <Route path="/evento/:slug/checkout" element={<CheckoutView />} />
          <Route path="/evento/:slug" element={<CheckoutView />} />
          <Route path="/pagar/:token" element={<PagamentoLink />} />
          <Route path="/evento-extra/:slug" element={<EventoEspecial />} />
          <Route path="/__acervo-preview" element={<AcervoPreview />} />

          {/* Telas anteriores, mantidas acessíveis para comparação e para
              voltar atrás sem reverter commit: basta trocar os elementos
              das três rotas públicas acima. */}
          <Route path="/v1" element={<Home />} />
          <Route path="/v1/faq" element={<FAQ />} />
          <Route path="/v1/sobre" element={<Sobre />} />

          {/* Apelidos do período de avaliação, para não quebrar links
              que já foram compartilhados. */}
          <Route path="/v2" element={<Navigate to="/" replace />} />
          <Route path="/v2/faq" element={<ParaSecaoDaHome padrao="#duvidas" />} />
          <Route path="/v2/sobre" element={<Navigate to="/sobre" replace />} />

          {/* Autenticação */}
          <Route path="/login" element={<LoginPage />} />
          <Route path="/cadastro" element={<CadastroPage />} />
          <Route path="/esqueci-senha" element={<EsqueciSenhaPage />} />
          <Route path="/redefinir-senha" element={<RedefinirSenhaPage />} />

          {/* Área da leitora */}
          <Route
            path="/minha-conta"
            element={
              <RotaUsuario>
                <MinhaContaLayout />
              </RotaUsuario>
            }
          >
            <Route index element={<ContaVisaoGeral />} />
            <Route path="eventos" element={<ContaMeusEventos />} />
            <Route path="assinatura" element={<ContaAssinatura />} />
            <Route path="pedidos" element={<ContaPedidos />} />
            <Route path="dados" element={<ContaDadosESenha />} />
          </Route>

          {/* Admin */}
          <Route path="/admin/login" element={<AdminLoginPage />} />
          <Route
            path="/admin/dashboard"
            element={
              <RotaProtegida>
                <AdminDashboardView />
              </RotaProtegida>
            }
          />
          <Route
            path="/admin/eventos"
            element={
              <RotaProtegida>
                <GestaoEventos />
              </RotaProtegida>
            }
          />
          <Route
            path="/admin/addons"
            element={
              <RotaProtegida>
                <GerenciadorAddons />
              </RotaProtegida>
            }
          />
          <Route
            path="/admin/eventos-especiais"
            element={
              <RotaProtegida>
                <EventosEspeciais />
              </RotaProtegida>
            }
          />
          <Route
            path="/admin/eventos-especiais/:eventoId/inscricoes"
            element={
              <RotaProtegida>
                <InscricoesPorEventoEspecial />
              </RotaProtegida>
            }
          />
          <Route
            path="/admin/inscricoes"
            element={
              <RotaProtegida>
                <InscricoesPorEvento />
              </RotaProtegida>
            }
          />
          <Route
            path="/admin/inscricoes/:eventoId"
            element={
              <RotaProtegida>
                <InscricoesPorEvento />
              </RotaProtegida>
            }
          />
          <Route
            path="/admin/membros"
            element={
              <RotaProtegida>
                <LilyMembers />
              </RotaProtegida>
            }
          />
          <Route
            path="/admin/assinantes"
            element={
              <RotaProtegida>
                <Assinantes />
              </RotaProtegida>
            }
          />
          <Route
            path="/admin/comprovantes"
            element={
              <RotaProtegida>
                <Comprovantes />
              </RotaProtegida>
            }
          />
          <Route
            path="/admin/livros"
            element={
              <RotaProtegida>
                <Livros />
              </RotaProtegida>
            }
          />
          <Route
            path="/admin/produtos"
            element={
              <RotaProtegida>
                <Produtos />
              </RotaProtegida>
            }
          />
          <Route
            path="/admin/pedidos"
            element={
              <RotaProtegida>
                <Pedidos />
              </RotaProtegida>
            }
          />

          {/* 404 */}
          <Route
            path="*"
            element={
              <div style={{ textAlign: 'center', padding: '4rem 1rem' }}>
                <h1 style={{ fontFamily: 'var(--font-display)' }}>404</h1>
                <p style={{ color: 'var(--clr-ink-muted)' }}>
                  Página não encontrada.
                </p>
                <Link
                  to="/"
                  style={{ color: 'var(--clr-rose)', fontWeight: 'bold' }}
                >
                  Voltar ao início
                </Link>
              </div>
            }
          />
        </Routes>
      </main>

      {!ocultarFooter && <Footer />}
    </>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <ScrollToTop />
      <AppContent />
    </BrowserRouter>
  );
}
